import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";

const lines = ["Stop force-fitting", "your business into", "someone else's", "software."];

const container = {
  animate: { transition: { staggerChildren: 0.12, delayChildren: 0.15 } },
};

const lineReveal = {
  initial: { y: "110%" },
  animate: {
    y: "0%",
    transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
  },
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const opacityFade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-[#FAFAFC] pt-[72px]"
    >
      <div className="hero-grid absolute inset-0" />
      <div
        className="absolute -top-40 right-[-10%] w-[640px] h-[640px] rounded-full opacity-[0.12] pointer-events-none"
        style={{
          background: "radial-gradient(circle, #0FA89E 0%, transparent 65%)",
        }}
      />

      <motion.div
        style={{ opacity: opacityFade }}
        className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-16 pb-24 lg:pt-24"
      >
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="font-mono-tech text-xs uppercase tracking-[0.25em] text-[#0FA89E] font-semibold mb-8"
        >
          May Forward Tech — AI & Digital Consultancy
        </motion.p>

        <motion.h1
          variants={container}
          initial="initial"
          animate="animate"
          data-testid="hero-headline"
          className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.08] text-[#0F172A] max-w-4xl"
        >
          {lines.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span variants={lineReveal} className="block">
                {i === 3 ? (
                  <span className="text-[#F5A400]">{line}</span>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
          data-testid="hero-subtext"
          className="mt-7 max-w-xl text-lg text-slate-600 leading-relaxed"
        >
          For decades, organizations have paid for software built for everyone
          — and adapted to none. Now we build custom tools and AI that work
          FOR you. Big Tech doesn't know your organization. We do.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.85, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <Link
            to="/contact"
            data-testid="hero-cta-primary"
            className="group inline-flex items-center gap-2.5 rounded-full bg-[#0FA89E] px-7 py-3.5 text-base font-semibold text-white shadow-[0_12px_35px_-10px_rgba(15,168,158,0.55)] transition-all hover:bg-[#0C8E85] hover:shadow-[0_16px_40px_-10px_rgba(15,168,158,0.6)]"
          >
            Build my tool
            <ArrowRight className="w-4.5 h-4.5 transition-transform group-hover:translate-x-1" />
          </Link>
          <Link
            to="/services"
            data-testid="hero-cta-secondary"
            className="inline-flex items-center gap-2.5 rounded-full border border-slate-300 bg-white px-7 py-3.5 text-base font-semibold text-slate-800 transition-all hover:border-[#0FA89E] hover:text-[#0FA89E]"
          >
            See what we build
          </Link>
        </motion.div>
      </motion.div>

      <motion.div
        style={{ y: yImg }}
        initial={{ opacity: 0, y: 60 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 1, ease: [0.22, 1, 0.36, 1] }}
        className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full"
      >
        <div className="relative rounded-2xl overflow-hidden border border-slate-200 shadow-[0_40px_80px_-30px_rgba(15,23,42,0.3)]">
          <img
            src="/img/hero-lines.png"
            alt="Line-art illustration of paths converging forward — May Forward Tech"
            className="w-full h-[280px] sm:h-[380px] lg:h-[460px] object-cover bg-white"
            data-testid="hero-image"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F19]/55 via-transparent to-transparent" />
          <div className="absolute bottom-6 left-6 sm:bottom-8 sm:left-8">
            <p className="font-mono-tech text-[11px] uppercase tracking-[0.25em] text-teal-300 mb-2">
              Est. Forward — Operating globally
            </p>
            <p className="font-display text-xl sm:text-2xl font-semibold text-white max-w-md">
              Custom-built for how you actually work.
            </p>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="relative flex justify-center py-8"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
          className="text-slate-400"
        >
          <ArrowDown className="w-5 h-5" />
        </motion.div>
      </motion.div>
    </section>
  );
};
